import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { AdminOverviewDto } from '@motoschool/shared';
import { formatDuration, formatMoney } from '@motoschool/shared';
import { api } from '../lib/api';
import { Avatar, Badge, Card, EmptyState, PageLoader, SectionTitle } from '../components/ui';

export function OverviewPage(): React.JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => api.get<AdminOverviewDto>('/admin/overview'),
  });

  if (isLoading) return <PageLoader />;
  if (!data) return <EmptyState title="Нет данных" />;

  const { totals } = data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Сводка</h1>
        <p className="text-sm text-muted">За последние 30 дней</p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Card className="p-3">
          <p className="text-xs text-muted">Собрано</p>
          <p className="mt-1 text-lg font-semibold tabular-nums">
            {formatMoney(totals.collectedMinor, data.currency)}
          </p>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-muted">Занятий проведено</p>
          <p className="mt-1 text-lg font-semibold tabular-nums">{totals.lessonsCompleted}</p>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-muted">Активных учеников</p>
          <p className="mt-1 text-lg font-semibold tabular-nums">{totals.activeStudents}</p>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-muted">Заявок ждут ответа</p>
          <p className="mt-1 text-lg font-semibold tabular-nums">
            {totals.pendingRequests}
            {totals.pendingRequests > 0 && (
              <Link to="/requests" className="ml-2 text-xs font-medium text-brand-600">
                открыть
              </Link>
            )}
          </p>
        </Card>
      </div>

      <section>
        <SectionTitle>Инструкторы</SectionTitle>
        {data.teachers.length === 0 ? (
          <EmptyState title="Инструкторов пока нет" />
        ) : (
          <div className="space-y-2">
            {data.teachers.map((item) => (
              <Card key={item.teacher.id} className="p-3">
                <div className="flex items-center gap-3">
                  <Avatar name={item.teacher.name} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{item.teacher.name}</p>
                    <p className="text-xs text-muted">
                      {item.activeStudents} учеников · {formatDuration(item.minutesTaught)} за период
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold tabular-nums">
                      {formatMoney(item.collectedMinor, data.currency)}
                    </p>
                    <p className="text-xs text-muted">
                      {item.lessonsCompleted} провёл · {item.lessonsUpcoming} впереди
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section>
        <SectionTitle>
          Должники
          {totals.debtMinutes < 0 && (
            <span className="ml-2 text-sm font-normal text-danger">
              всего {formatDuration(totals.debtMinutes)}
            </span>
          )}
        </SectionTitle>

        {data.debtors.length === 0 ? (
          <EmptyState title="Должников нет" description="У всех учеников положительный баланс часов." />
        ) : (
          <div className="space-y-2">
            {data.debtors.map((item) => (
              <Link key={item.student.id} to={`/students/${item.student.id}`} className="block">
                <Card className="p-3 transition-colors hover:border-brand-200">
                  <div className="flex items-center gap-3">
                    <Avatar name={item.student.name} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{item.student.name}</p>
                      <p className="text-xs text-muted">{item.student.phone ?? '—'}</p>
                    </div>
                    <Badge tone="danger">{formatDuration(item.balanceMinutes)}</Badge>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
